import { Component, type ReactNode, type ErrorInfo } from "react";
import { TriangleAlert } from "@/shared/components/icons";
import { Button } from "@/shared/components/ui/Button";
import { Link } from "@/shared/components/ui/Link";
import { Card } from "@/shared/components/ui/Card";
import { messages } from "@/shared/i18n";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error Boundary Component
 *
 * Catches JavaScript errors anywhere in the child component tree,
 * logs the errors, and displays a fallback UI instead of crashing.
 *
 * @example
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 *
 * @example With custom fallback
 * <ErrorBoundary fallback={(error, reset) => <CustomError error={error} onReset={reset} />}>
 *   <App />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error to console (in production, send to error tracking service)
    console.error("Error Boundary caught an error:", error, errorInfo);

    // TODO: Send to error tracking service (Sentry, LogRocket, etc.)
    // logErrorToService(error, errorInfo);
  }

  resetError = (): void => {
    this.setState({
      hasError: false,
      error: null,
    });
  };

  override render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.resetError);
      }

      // Default fallback UI — uses bg-gradient-page token for consistency with AuthLayout
      return (
        <div className="bg-gradient-page flex min-h-screen items-center justify-center px-4 py-12">
          <Card className="w-full max-w-md">
            <div className="text-center">
              {/* Error Icon */}
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
                <TriangleAlert className="h-8 w-8 text-red-600" />
              </div>

              {/* Title */}
              <h1 className="mb-2 text-2xl font-bold text-gray-900">
                {messages.components.errorBoundary.title}
              </h1>

              {/* Description */}
              <p className="mb-6 text-gray-600">{messages.components.errorBoundary.message}</p>

              {/* Error Details (only in development) */}
              {import.meta.env.DEV && (
                <details className="mb-6 rounded-lg bg-red-50 p-4 text-left">
                  <summary className="cursor-pointer font-medium text-red-900">
                    {messages.components.errorBoundary.technicalDetails}
                  </summary>
                  <pre className="mt-3 overflow-auto text-xs text-red-800">
                    {this.state.error.toString()}
                    {this.state.error.stack && `\n\n${this.state.error.stack}`}
                  </pre>
                </details>
              )}

              {/* Actions */}
              <div className="flex flex-col gap-3">
                <Button fullWidth onClick={this.resetError}>
                  {messages.components.errorBoundary.retryButton}
                </Button>
                <Link className="w-full" external={false} href="/" variant="buttonSecondary">
                  {messages.pages.notFound.goHomeButton}
                </Link>
              </div>
            </div>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
